import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FormEvent, useState } from 'react';
import { clientEnv } from '@/lib/env';
import { getInstallationId, learnerApiJson, type GetToken } from '@/lib/learner-api';
import { track } from '@/lib/analytics';
import { typeScale } from '@/lib/typography';

type Message = { id: string; author: 'user' | 'admin'; body: string; created_at: string };
type ThreadResponse = { thread: { id: string } | null; messages: Message[] };

export const Route = createFileRoute('/app/support')({ component: SupportRoute });

function SupportRoute() {
  if (!clientEnv.clerkPublishableKey) return <SupportChat getToken={null} />;
  return <ClerkSupport />;
}

function ClerkSupport() {
  const { getToken } = useAuth();
  return <SupportChat getToken={getToken} />;
}

function SupportChat({ getToken }: { getToken: GetToken | null }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const queryClient = useQueryClient();
  const installationId = getInstallationId();
  const thread = useQuery({
    queryKey: ['support-thread', installationId],
    queryFn: () => learnerApiJson<ThreadResponse>(getToken, `/v1/support/thread?installation_id=${installationId}`),
    refetchInterval: 30_000,
  });
  const send = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await learnerApiJson(getToken, '/v1/support/messages', {
        method: 'POST',
        body: JSON.stringify({
          installation_id: installationId,
          message: trimmed,
          client_message_uuid: crypto.randomUUID(),
          platform: 'web',
        }),
      });
      setMessage('');
      track('Support Message Sent');
      await queryClient.invalidateQueries({ queryKey: ['support-thread', installationId] });
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="mx-auto flex min-h-[calc(100vh-6rem)] max-w-3xl flex-col px-5 py-8 sm:px-8">
      <h1 className={typeScale.supportTitle}>Support</h1>
      <div className="mt-6 flex-1 space-y-3 rounded-3xl border border-[#cfe8c6] bg-white p-5">
        {thread.isLoading && <p className="text-slate-500">Loading conversation…</p>}
        {!thread.data?.messages.length && !thread.isLoading && <p className="text-slate-500">Send us a message and the Hmonglus team will reply here.</p>}
        {thread.data?.messages.map((item) => (
          <div key={item.id} className={`flex ${item.author === 'user' ? 'justify-end' : 'justify-start'}`}>
            <p className={`max-w-[85%] rounded-2xl px-4 py-3 ${item.author === 'user' ? 'bg-[#399918] text-white' : 'bg-slate-100 text-slate-800'}`}>{item.body}</p>
          </div>
        ))}
      </div>
      <form onSubmit={(event) => void send(event)} className="mt-4 flex gap-3">
        <label className="sr-only" htmlFor="support-message">Message</label>
        <input id="support-message" value={message} onChange={(event) => setMessage(event.target.value)} maxLength={2000} placeholder="Write a message" className="min-w-0 flex-1 rounded-2xl border border-[#b7dfb0] bg-white px-4 py-3 outline-none focus:ring-4 focus:ring-[#399918]/15" />
        <button disabled={sending || !message.trim()} className="rounded-2xl bg-[#399918] px-5 font-bold text-white disabled:opacity-50">Send</button>
      </form>
    </div>
  );
}
